import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarToday, ArrowForward } from '@mui/icons-material';

const PageContainer = styled.div`
  background-color: var(--color-bg);
  min-height: 100vh;
  padding: 120px 24px;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: clamp(3rem, 6vw, 5rem);
  margin-bottom: 24px;
  color: var(--color-text);
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--color-text-light);
  max-width: 700px;
  margin: 0 auto;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 60px;
`;

const FilterButton = styled.button`
  padding: 8px 20px;
  border-radius: 50px;
  border: 1px solid ${props => props.active ? 'var(--color-primary-blue)' : 'rgba(0,0,0,0.1)'};
  background: ${props => props.active ? 'var(--color-primary-blue)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--color-text-light)'};
  font-family: var(--font-sans);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--color-primary-blue);
    color: ${props => props.active ? 'white' : 'var(--color-primary-blue)'};
  }
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 32px;
`;

const EventCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 40px rgba(0,0,0,0.08);
    border-color: rgba(0,0,0,0.1);
  }
`;

const CardContent = styled.div`
  padding: 32px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const MetaTags = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  font-size: 0.85rem;
  color: var(--color-text-light);
`;

const CategoryTag = styled.span`
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  color: var(--color-primary-blue);
`;

const CardTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  margin-bottom: 12px;
  color: var(--color-text);
  line-height: 1.3;
`;

const CardDesc = styled.p`
  color: var(--color-text-light);
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 24px;
  flex-grow: 1;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(0,0,0,0.05);
  padding-top: 20px;
  margin-top: auto;
`;

const DateDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-weight: 500;
`;

const events = [
  {
    title: "Chalets de l'Avent",
    description: "Come and enjoy a warm cup of hot chocolate and delicious souvganiotes with us!",
    link: "/chalets2024",
    date: "2024-12-11",
    category: "Social"
  },
  {
    title: "Start-up Ecosystem in Israel",
    description: "A conference by Dr. Laurent Choppe on the Life Sciences landscape.",
    link: "/conference1",
    date: "2024-12-05",
    category: "Conference"
  },
  {
    title: "Rosh Hashana 2024",
    description: "Dip some apples in honey and wish for a sweet year ahead!",
    link: "/rosh_hashana2024",
    date: "2024-10-01",
    category: "Cultural"
  },
  {
    title: "Semaine des Associations",
    description: "Discover what we do and get involved with our community during EPFL Associations Week.",
    link: "/semDesAss24",
    date: "2024-09-24",
    category: "Community"
  },
  {
    title: "Welcome Apero",
    description: "The perfect way to meet new members and create a sense of community!",
    link: "/welcomeApero",
    date: "2024-09-19",
    category: "Social"
  },
  {
    title: "Vivapoly 2024",
    description: "We are happy to announce that we will take part in the 2024 Vivapoly edition!",
    link: "/vivap24",
    date: "2024-05-20",
    category: "Community"
  },
  {
    title: "Vivapoly 2025",
    description: "Second appearance at Vivapoly! We are excited to meet new students.",
    link: "/vivap25",
    date: "2025-05-22",
    category: "Community"
  },
  {
    title: "Creation of SHALOM",
    description: "The Israeli Association has been established. Excited to develop it further!",
    link: "/creation",
    date: "2024-04-08",
    category: "Milestone"
  },
  {
    title: "Middle East Relations",
    description: "Conference by Prof. Denis Charbit on the history of international relations.",
    link: "/conference2",
    date: "2025-03-05",
    category: "Conference"
  },
  {
    title: "Community Shabbat",
    description: "Celebrate Shabbat with Shalom! Open to everyone.",
    link: "/shabbat",
    date: "2025-04-04",
    category: "Cultural"
  },
];

const Activities = () => {
  const [filter, setFilter] = useState('All');

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isUpcoming = (dateStr) => new Date(dateStr) > new Date();

  const sortedEvents = [...events].sort((a, b) => new Date(b.date) - new Date(a.date));
  const filteredEvents = filter === 'All' ? sortedEvents : sortedEvents.filter(e => e.category === filter);
  const categories = ['All', ...new Set(events.map(e => e.category))];

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderSection>
          <PageTitle>Associative Life</PageTitle>
          <PageSubtitle>
            Discover our vibrant community through meaningful events, cultural celebrations, and academic excellence.
          </PageSubtitle>
        </HeaderSection>

        <FilterContainer>
          {categories.map(cat => (
            <FilterButton
              key={cat}
              active={filter === cat}
              onClick={() => setFilter(cat)}
            >
              {cat}
            </FilterButton>
          ))}
        </FilterContainer>

        <EventsGrid>
          <AnimatePresence>
            {filteredEvents.map((event, i) => (
              <Link to={event.link} style={{ textDecoration: 'none' }} key={i}>
                <EventCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <CardContent>
                    <MetaTags>
                      <CategoryTag>{event.category}</CategoryTag>
                      {isUpcoming(event.date) && <span style={{ color: '#50E3C2', fontWeight: 'bold', fontSize: '12px' }}>UPCOMING</span>}
                    </MetaTags>
                    <CardTitle>{event.title}</CardTitle>
                    <CardDesc>{event.description}</CardDesc>

                    <CardFooter>
                      <DateDisplay>
                        <CalendarToday fontSize="small" />
                        {formatDate(event.date)}
                      </DateDisplay>
                      <div style={{ color: 'var(--color-primary-blue)' }}>
                        <ArrowForward />
                      </div>
                    </CardFooter>
                  </CardContent>
                </EventCard>
              </Link>
            ))}
          </AnimatePresence>
        </EventsGrid>

      </ContentWrapper>
    </PageContainer>
  );
};

export default Activities;